import { ApolloClient, ApolloLink, createHttpLink, InMemoryCache, makeVar } from "@apollo/client";
import { Observable } from "@apollo/client/core";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from '@apollo/client/link/context';
import { jwtDecode } from "jwt-decode";
const uri = process.env.WF_GRAPHQL_API || "https://dev.api.withfriends.co/graphql";
const httpLink = createHttpLink({ uri });
const authLink = setContext((_, { headers }) => {
    const token = document.documentElement.dataset.withfriendsToken;
    return {
        headers: Object.assign(Object.assign({}, headers), { authorization: token ? `Bearer ${token}` : "" })
    };
});
const retryLink = new RetryLink({
    delay: {
        initial: 500,
        max: Infinity,
        jitter: true
    },
    attempts: {
        max: 10,
        retryIf: (error, _operation) => {
            const willRetry = !!error && !error.message.includes('400');
            if (willRetry)
                console.log("retrying", error.message);
            return willRetry;
        }
    }
});
const gatewayRetryLink = new ApolloLink((operation, forward) => {
    return forward(operation).map((data) => {
        var _a;
        if ((_a = data === null || data === void 0 ? void 0 : data.errors) === null || _a === void 0 ? void 0 : _a.find(({ message }) => message.includes("request to") && message.includes("failed") && message.includes("reason"))) {
            throw new Error('Gateway internal error');
        }
        return data;
    });
});
// Track in-flight GraphQL operations so we can wait for network idle before clearing the store
let inflightOperationCount = 0;
let idleResolvers = [];
const resolveIdleIfNeeded = () => {
    if (inflightOperationCount === 0 && idleResolvers.length > 0) {
        const resolvers = [...idleResolvers];
        idleResolvers = [];
        resolvers.forEach((resolve) => resolve());
    }
};
export const waitForNetworkIdle = (timeoutMs = 15000) => {
    if (inflightOperationCount === 0)
        return Promise.resolve();
    return new Promise((resolve, reject) => {
        const onIdle = () => {
            window.clearTimeout(timer);
            resolve();
        };
        const timer = window.setTimeout(() => {
            idleResolvers = idleResolvers.filter((r) => r !== onIdle);
            reject(new Error('Timed out waiting for network idle'));
        }, timeoutMs);
        idleResolvers.push(onIdle);
        // If things already became idle between scheduling and push, resolve now
        resolveIdleIfNeeded();
    });
};
const networkStatusLink = new ApolloLink((operation, forward) => {
    inflightOperationCount++;
    const observable = forward(operation);
    return new Observable((observer) => {
        let finalized = false;
        const finalize = () => {
            if (finalized)
                return;
            finalized = true;
            inflightOperationCount = Math.max(0, inflightOperationCount - 1);
            resolveIdleIfNeeded();
        };
        const sub = observable.subscribe({
            next: (value) => observer.next(value),
            error: (err) => {
                finalize();
                observer.error(err);
            },
            complete: () => {
                finalize();
                observer.complete();
            },
        });
        return () => {
            finalize();
            sub.unsubscribe();
        };
    });
});
// KGAUDIT - Why are we deleting typename
const cleanTypeName = new ApolloLink((operation, forward) => {
    if (operation.variables) {
        const omitTypename = (key, value) => (key === '__typename' ? undefined : value);
        operation.variables = JSON.parse(JSON.stringify(operation.variables), omitTypename);
    }
    return forward(operation).map((data) => {
        return data;
    });
});
const linkMe = ApolloLink.from([
    networkStatusLink,
    cleanTypeName,
    authLink,
    retryLink,
    gatewayRetryLink,
    httpLink,
]);
export const withfriendsRoot = process.env.WF_ROOT || "https://kg-shopify-4-every2.ngrok.io";
export const currentBusinessIdVar = makeVar(null);
export const currentBusinessHasTooManyProductsVar = makeVar(false);
export const tooManyProductsThreshold = 1550;
export const currentShopifyVersionVar = makeVar(null);
export const primaryActionLoadingVar = makeVar(false);
export const showTimeoutVar = makeVar(false);
export const currentSubscriptionBenefitVar = makeVar(null);
export const currentWriteInBenefitVar = makeVar(null);
export const currencySymbolVar = makeVar('$');
export const currencyCodeVar = makeVar('USD');
export const membershipsHaveBeenEditedVar = makeVar(false);
export const pageHasUnsavedChangesVar = makeVar(false);
export const createAccountJobIdVar = makeVar(document.documentElement.dataset.createAccountJobId);
export const currentShopifyShopVar = makeVar(null);
export const currentNewlyCreatedDefaultProductUUIDsVar = makeVar([]);
export const currentShopifyProductVariantsVar = makeVar(null);
export const currentCurationsVar = makeVar({});
export const activeFulfillmentIdVar = makeVar(null);
export const showExportShippingLabelsVar = makeVar(false);
export const saveToFutureFulfillmentsVar = makeVar(true);
export const activeDropdownVar = makeVar(null);
export const focusedBenefitIdVar = makeVar(null);
export const getCurrentUserId = () => {
    var _a, _b;
    let token = (_a = document.querySelector('meta[name="withfriends:t"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content');
    if (!token) {
        console.log('no token found');
        return null;
    }
    const payload = jwtDecode(token);
    const userId = (_b = payload.sub) === null || _b === void 0 ? void 0 : _b.toString();
    if (!userId) {
        console.log('no user found in token');
        return null;
    }
    return userId;
};
const primaryActionTimeoutDelay = 30000;
const primaryActionTimeout = makeVar(null);
export const startPrimaryAction = () => {
    primaryActionLoadingVar(true);
    const timeout = primaryActionTimeout();
    if (timeout)
        window.clearTimeout(timeout);
    primaryActionTimeout(window.setTimeout(() => showTimeoutVar(primaryActionLoadingVar()), primaryActionTimeoutDelay));
};
export const completePrimaryAction = () => {
    primaryActionLoadingVar(false);
    const timeout = primaryActionTimeout();
    if (timeout)
        window.clearTimeout(timeout);
    showTimeoutVar(false);
};
const cache = new InMemoryCache({
    typePolicies: {
        Query: {
            fields: {
                subscriptionBenefit: {
                    read: (_, { args, toReference }) => {
                        return toReference({
                            __typename: "SubscriptionBenefit",
                            id: args.id,
                        });
                    }
                },
                currentUserId: {
                    read: () => {
                        return getCurrentUserId();
                    }
                },
                currentBusinessId: {
                    read: () => {
                        return currentBusinessIdVar();
                    },
                },
                currentBusinessHasTooManyProducts: {
                    read: () => {
                        return currentBusinessHasTooManyProductsVar();
                    },
                },
                currentShopifyVersion: {
                    read: () => {
                        return currentShopifyVersionVar();
                    }
                },
                currentShopifyShop: {
                    read: () => {
                        return currentShopifyShopVar();
                    },
                },
                currentSubscriptionBenefit: {
                    read: () => {
                        return currentSubscriptionBenefitVar();
                    },
                },
                primaryActionLoading: {
                    read: () => {
                        return primaryActionLoadingVar();
                    },
                },
                activeFulfillmentId: {
                    read: () => {
                        return activeFulfillmentIdVar();
                    },
                },
                exportShippingLabels: {
                    read: () => {
                        return showExportShippingLabelsVar();
                    },
                },
            },
        },
        Price: {
            fields: {
                symbol: {
                    read: () => {
                        return currencySymbolVar() || "$";
                    }
                }
            }
        },
        MembershipTier: {
            merge: true,
        },
        SubscriptionBenefit: {
            merge: true,
        },
        WelcomeGiftBenefit: {
            merge: true,
        },
        Address: {
            merge: true,
        },
        Fulfillment: {
            merge: true,
            fields: {
                address: {
                    merge: (existing = {}, incoming, { mergeObjects }) => {
                        return mergeObjects(existing, incoming);
                    }
                }
            }
        }
    },
    possibleTypes: {
        Benefit: ["RegularBenefit", "SubscriptionBenefit", "WelcomeGiftBenefit"]
    },
});
const client = new ApolloClient({
    link: linkMe,
    cache: cache,
    name: 'React Front-End',
    version: '1.0',
    connectToDevTools: true,
});
export const dummyFunc = ({}) => { return [() => { }]; };
export default client;
